import { inject, Injectable } from '@angular/core';
import { catchError, forkJoin, Observable, of, take } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiHttpService } from '../../api-http.service';
import { ClientOrderItem, CustomHttpResponse, DocumentSections } from '../../../models';
import { SectionsFor } from '../../../types';

@Injectable({
  providedIn: 'root',
})
export class ClientOrderItemService {
  apiService: ApiHttpService<ClientOrderItem> = inject(ApiHttpService<ClientOrderItem>);

  getClientOrderItemByIri(iri: string): Observable<ClientOrderItem> {
    return this.apiService.get(iri).pipe(
      take(1),
      map((result: CustomHttpResponse<ClientOrderItem>) => result.body),
    );
  }

  getClientOrderItemById(id: string): Observable<ClientOrderItem> {
    return this.apiService.get(`/client_order_items/${id}`).pipe(
      take(1),
      map((result: CustomHttpResponse<ClientOrderItem>) => result.body),
    );
  }

  createClientOrderItem(clientOrderItem: ClientOrderItem): Observable<ClientOrderItem> {
    return this.apiService
      .post(`/client_order_items`, clientOrderItem)
      .pipe(map((result: CustomHttpResponse<ClientOrderItem>) => result.body));
  }

  getClientOrderItemsForClientOrder(iri: string): Observable<ClientOrderItem[]> {
    return this.apiService.getMany(`${iri}/client_order_items`).pipe(map((result) => result.body['hydra:member']));
  }

  updateClientOrderItem(
    iri: string,
    updatedRecord: Record<string, string | number | null | boolean | undefined> | ClientOrderItem,
  ): Observable<ClientOrderItem> {
    return this.apiService.patch(iri, updatedRecord as unknown as ClientOrderItem).pipe(
      take(1),
      map((result: CustomHttpResponse<ClientOrderItem>) => result.body),
    );
  }

  getForSupplierInvoiceSection(iris: string[]): Observable<DocumentSections<ClientOrderItem>> {
    const requests = iris.map((iri) =>
      this.apiService.getMany(`${iri}/client_order_items`).pipe(
        map((res) => ({ iri, items: res.body['hydra:member'] as ClientOrderItem[] })),
        // keep the key even if one call fails
        catchError(() => of({ iri, items: [] })),
      ),
    );

    return forkJoin(requests).pipe(
      map((pairs) => {
        // Start as a partial of the exact-keys type, then return it
        const acc: Partial<SectionsFor<readonly string[], ClientOrderItem>> = {};
        for (const { iri, items } of pairs) acc[iri] = items;
        return acc as SectionsFor<typeof iris, ClientOrderItem>;
      }),
    );
  }

  getForClientOrders(clientOrderIris: readonly string[]): Observable<DocumentSections<ClientOrderItem>> {
    const requests = clientOrderIris.map((iri) =>
      this.apiService.getMany(`${iri}/client_order_items`).pipe(
        map((res: { body: { 'hydra:member': ClientOrderItem[] } }) => ({
          iri,
          items: res.body['hydra:member'],
        })),
        catchError(() => of({ iri, items: [] as ClientOrderItem[] })),
      ),
    );

    return forkJoin(requests).pipe(
      map((pairs) => {
        const acc: DocumentSections<ClientOrderItem> = {};
        for (const { iri, items } of pairs) acc[iri] = items;
        return acc;
      }),
    );
  }

  delete(clientOrderItem: ClientOrderItem) {
    return this.apiService.delete(`/client_order_items/${clientOrderItem.id}`).pipe(map((result) => result));
  }
}
